import React from "react";
import clsx from "clsx";
import {
  FilterNotifications,
  NotificationMethod,
  NOTIFICATION_BACKEND_SOURCES,
  NOTIFICATION_BACKEND_SOURCE_LABELS,
  isNotificationMethodEnabled,
  withNotificationMethod,
} from "@/types/notifications";

const METHODS: NotificationMethod[] = ["email", "sound", "popup"];

const METHOD_LABELS: Record<NotificationMethod, string> = {
  email: "Email",
  sound: "Sound",
  popup: "Pop-Up",
};

interface NotificationMethodCheckboxesProps {
  value: FilterNotifications | undefined;
  onChange: (next: FilterNotifications) => void;
  /** Methods rendered disabled with a "(Coming Soon)" suffix */
  comingSoonMethods?: NotificationMethod[];
  /** Called when a method is being switched on (e.g. to request browser permission) */
  onEnableMethod?: (method: NotificationMethod) => void;
  /** "modal" for light/dark modal forms, "pane" for the dark side-pane forms */
  variant?: "modal" | "pane";
}

/**
 * Email/Sound/Pop-Up checkboxes rendered per notification backend source.
 * Only "newsquawk-headlines" exists today; new backend sources appear
 * automatically once added to NOTIFICATION_BACKEND_SOURCES.
 */
export default function NotificationMethodCheckboxes({
  value,
  onChange,
  comingSoonMethods,
  onEnableMethod,
  variant = "modal",
}: NotificationMethodCheckboxesProps) {
  const isModal = variant === "modal";
  const sourceLabelClass = isModal
    ? "text-2xs sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1"
    : "text-2xs font-medium text-gray-500 uppercase tracking-wide mb-1.5";
  const rowGapClass = isModal ? "gap-1.5 sm:gap-2" : "gap-2";
  const listClass = isModal ? "space-y-1.5 sm:space-y-2" : "space-y-2";
  const checkboxClass = isModal
    ? "rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-squawk-blue h-4 w-4"
    : "size-3.5 rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-0 focus:ring-offset-0";
  const textClass = isModal
    ? "text-xs sm:text-sm text-gray-700 dark:text-gray-300"
    : "text-sm text-gray-300";

  return (
    <div className="space-y-3">
      {NOTIFICATION_BACKEND_SOURCES.map((source) => (
        <div key={source}>
          <div className={sourceLabelClass}>
            {NOTIFICATION_BACKEND_SOURCE_LABELS[source]}
          </div>
          <div className={listClass}>
            {METHODS.map((method) => {
              const comingSoon = comingSoonMethods?.includes(method) ?? false;
              return (
                <label
                  key={method}
                  className={clsx(
                    "flex items-center",
                    rowGapClass,
                    comingSoon
                      ? "opacity-40 pointer-events-none"
                      : "cursor-pointer"
                  )}
                >
                  <input
                    type="checkbox"
                    disabled={comingSoon}
                    checked={isNotificationMethodEnabled(value, method, source)}
                    onChange={(e) => {
                      if (e.target.checked) onEnableMethod?.(method);
                      onChange(
                        withNotificationMethod(
                          value,
                          method,
                          e.target.checked,
                          source
                        )
                      );
                    }}
                    className={checkboxClass}
                  />
                  <span className={textClass}>
                    {METHOD_LABELS[method]}
                    {comingSoon ? " (Coming Soon)" : ""}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
